import { ChangeDetectionStrategy, Component, OnInit, inject, input, output, signal } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { niceBytes } from './upload-file.utils';

interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

@Component({
  selector: 'nk-upload-file',
  imports: [TranslateModule],
  templateUrl: './upload-file.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadFileComponent implements OnInit {
  // outputs
  public fileTransferredSuccess = output<File>();
  public fileTransferredError = output<Error>();
  public fileTransferInProgress = output<boolean>();
  public fileRemove = output<File>();

  // inputs (signals)
  public initialFile = input<File | null>(null);
  public maxFileSize = input<number | null>(null);
  public validFileExtension = input<string[] | null>(null);
  public clearFileAfterSuccess = input<boolean>(false);

  // local state (signals)
  public uploadedFile = signal<File | null>(null);
  public browsedFile = signal<File | null>(null);

  private _translateService = inject(TranslateService);

  public ngOnInit(): void {
    const initial = this.initialFile();
    if (initial) {
      this.uploadedFile.set(initial);
    }
  }

  public removeFile(emitToParent = true): void {
    if (!emitToParent) {
      const current = this.uploadedFile();
      if (current) {
        this.fileRemove.emit(current);
      }
    }
    this.uploadedFile.set(null);
    this.browsedFile.set(null);
  }

  public onDragOver(ev: DragEvent): void {
    // The event onDrop event fires only when
    // onDragOver has preventDefault() and stopPropagation() methods run on event.
    ev.preventDefault();
    ev.stopPropagation();
  }

  // important we have two cases. file being dragged in area (onDrop), and file being browsed (onBrowse).
  public onDrop(event: DragEvent): void {
    this.fileTransferInProgress.emit(true);
    event.preventDefault();
    this.uploadedFile.set(null);
    const { dataTransfer } = event;
    // NOTE: the user can drag and drop anything, including html elements, so first we check for this.
    const files: File[] = [];
    if (dataTransfer?.items) {
      for (let i = 0; i < dataTransfer.items.length; i++) {
        // If dropped items aren't files, reject them
        if (dataTransfer.items[i].kind === 'file') {
          const file = dataTransfer.items[i].getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 1) {
        const errorMessage = this._translateService.instant(
          extractTranslation('upload_file.only_one_file_is_allowed'),
        ) as string;
        this.fileTransferInProgress.emit(false);
        this.fileTransferredError.emit(new Error(errorMessage));
      } else {
        if (this.checkValidFile(files[0])) {
          this.onFileUpload(files[0]);
        } else {
          this.uploadedFile.set(null);
        }
      }
      dataTransfer.items.clear();
    } else {
      dataTransfer?.clearData();
    }
  }

  public onFileBrowse(event: Event): void {
    this.fileTransferInProgress.emit(true);

    this.uploadedFile.set(null);
    const inputElement = (event as HTMLInputEvent).target;
    const file = inputElement.files?.[0];
    if (file && this.checkValidFile(file)) {
      this.onFileUpload(file);
    }
    // Reset the input value so re-selecting the same file triggers the change event
    inputElement.value = '';
  }

  public checkValidFile(file: File): boolean {
    const maxFileSize = this.maxFileSize() ?? 0;
    if (maxFileSize && file.size > maxFileSize) {
      const currentSize = niceBytes(file.size);
      const maxFileSize = niceBytes(this.maxFileSize());
      const errorMessage = this._translateService.instant(extractTranslation('upload_file.file_is_too_large'), {
        currentSize,
        maxFileSize,
      }) as string;
      this.fileTransferredError.emit(new Error(errorMessage));
      this.fileTransferInProgress.emit(false);

      return false;
    } else if ((this.validFileExtension()?.length ?? 0) > 0) {
      const fileName = file.name;
      const parts = fileName.split('.');
      const fileExtension = parts[parts.length - 1];
      if (!this.validFileExtension()?.includes(fileExtension)) {
        const errorMessage = this._translateService.instant(extractTranslation('upload_file.wrong_file_type'), {
          validFileExtensions: this.validFileExtension()?.join(', '),
        }) as string;
        this.fileTransferredError.emit(new Error(errorMessage));
        this.fileTransferInProgress.emit(false);
        return false;
      }
    }
    return true;
  }

  public onFileUpload(file: File): void {
    this.uploadedFile.set(file);
    this.fileTransferredSuccess.emit(file);
    this.fileTransferInProgress.emit(false);

    if (this.clearFileAfterSuccess()) {
      this.removeFile(true);
    }
  }
}
